import React, {useCallback, useEffect, useState} from "react"
import {useNavigate, Location} from "react-router-dom"
import {Link as MuiLink, Table, TableBody, TableCell, TableRow, Box, Chip} from "@mui/material"

import {api, Customer, CustomerSearchResponse, CustomerSearchResult} from "@indebted/api"
import {Search} from "@indebted/components/Search"
import {Layout} from "@indebted/components/Layout"
import {SystemOfRecordChip} from "@indebted/components/SystemOfRecordChip"

function CustomersPage({location}: {location: Location}) {
	const query = new URLSearchParams(location.search).get("q")
	return <Layout>{query ? <WithQuery query={query} location={location} /> : <WithoutQuery />}</Layout>
}

function WithoutQuery() {
	return (
		<>
			<Search value={""} loading={false} error={""} />
		</>
	)
}

function resolveCustomerPath(customer: Customer): string {
	const base = `/customers/${customer.ID}`
	const totalAccounts =
		customer.OutstandingAccounts.length + customer.OtherAccounts.length + customer.PaymentPlanAccounts.length
	if (totalAccounts === 1) {
		if (customer.OutstandingAccounts.length === 1) {
			return `${base}/accounts/outstanding/${customer.OutstandingAccounts[0].ID}`
		}
		if (customer.OtherAccounts.length === 1) {
			return `${base}/accounts/other/${customer.OtherAccounts[0].ID}`
		}
		if (customer.PaymentPlanAccounts.length === 1) {
			return `${base}/accounts/payment-plan/${customer.PaymentPlanAccounts[0].ID}`
		}
	}
	return base
}

function WithQuery({query, location}: {query: string; location: Location}) {
	const [state, setState] = useState<{customers: CustomerSearchResponse; loading: boolean; error: string}>({
		customers: [],
		loading: false,
		error: "",
	})

	const navigate = useNavigate()

	const navigateToCustomer = useCallback(
		(searchResult: CustomerSearchResult) => {
			setState((prev) => ({...prev, loading: true}))
			api.customer
				.get(searchResult.ID, new Date().getTimezoneOffset())
				.then((customer) => {
					const path = resolveCustomerPath(customer)
					navigate(
						`${path}?q=${encodeURIComponent(query)}&back=/customers${encodeURIComponent(location.search)}`,
					)
				})
				.catch(() => {
					setState((prev) => ({...prev, loading: false}))
					navigate(
						`/customers/${searchResult.ID}?q=${encodeURIComponent(query)}&back=/customers${encodeURIComponent(location.search)}`,
					)
				})
		},
		[query, location, navigate],
	)

	useEffect(() => {
		setState({customers: [], loading: true, error: ""})
		api.customer
			.search(query)
			.then((customers) => {
				setState({customers: customers, loading: false, error: ""})
				if (customers.length === 1) {
					navigateToCustomer(customers[0])
				}
			})
			.catch((error) => {
				setState({customers: [], loading: false, error: error.Message})
			})
	}, [query, navigateToCustomer])

	return (
		<>
			<Search value={query} loading={state.loading} error={state.error} />
			<Table>
				<TableBody>
					{state.customers.map((customer) => (
						<TableRow
							key={customer.ID}
							style={{cursor: "pointer"}}
							onClick={() => navigateToCustomer(customer)}
						>
							<TableCell>
								<Box display="flex" alignItems="center">
									<MuiLink component="span">{customer.FullName}</MuiLink>
									<SystemOfRecordChip systemOfRecord={customer.SystemOfRecord} />
									{customer.CollectionHold && (
										<Chip
											label="Collections on hold"
											size="medium"
											sx={{
												backgroundColor: "#E67E22",
												color: "#FFFFFF",
												border: "none",
												marginLeft: 2,
											}}
										/>
									)}
								</Box>
							</TableCell>
							<TableCell align="left">{customer.DOB}</TableCell>
							<TableCell align="right">{customer.ClientName}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</>
	)
}

export {CustomersPage}
